<template>
  <div
    :key="`${simulationId}-${roundIndex}-${item.id}`"
    class="impulse-sourceinfo"
  >
    <!-- TITLE AND TYPE -->
    <template v-if="showTitle">
      <h2 class="title impulse-sourceinfo__title">
        <span>{{ itemTitle || $t("labels.unnamed") }}</span>
        <button
          v-if="item.type != 'scheduledEffect'"
          type="button"
          class="button-icon button__infoicon p-0"
          @click="openOverlay(item)"
        >
          <lucide-icon name="info" />
        </button>
      </h2>

      <slot name="simulation-title"></slot>
    </template>

    <div class="impulse-sourceinfo__content">
      <section v-if="!isActivated" class="impulse-sourceinfo__section">
        <p class="mv-0">
          <template v-if="item.type === 'action'">
            {{ $t("texts.actionNotPlayedYet") }}
          </template>
          <template v-else-if="item.type === 'scheduledEffect'">
            {{ $t("texts.impulseNotPlayedYet") }}
          </template>
        </p>
      </section>

      <!-- REASONS -->
      <section
        v-if="incidentReason != null"
        class="impulse-sourceinfo__section"
      >
        <div class="subtitle">
          {{ $t("labels.occurrenceReason") }}
        </div>
        <template
          v-if="
            incidentReason.triggerId != null &&
            triggers[incidentReason.triggerId]
          "
        >
          <trigger-title
            :trigger="triggers[incidentReason.triggerId]"
          />
          <formatted-text
            v-if="!isEmpty(triggers[incidentReason.triggerId].description)"
            class="message-info"
            :value="triggers[incidentReason.triggerId].description"
          />
          <div
            v-if="
              incidentReason.neededChance < 100 &&
              incidentReason.rolledChance <= incidentReason.neededChance
            "
          >
            {{
              $t("texts.incidentOccurredWithChanceX", [
                incidentReason.neededChance
              ])
            }}
          </div>
          <div v-else-if="incidentReason.conditionId == null">
            {{ $t("texts.triggeredWithoutConditions") }}
          </div>
        </template>
        <template v-else-if="incidentReason.conditionId != null">
          <condition-label :condition-id="incidentReason.conditionId" />
        </template>
        <template v-else>
          <p class="mt-1">
            {{
              $t("texts.incidentOccurredWithChanceX", [
                incidentReason.neededChance
              ])
            }}
          </p>
        </template>
      </section>

      <section
        v-if="preventingConditions.length > 0"
        class="impulse-sourceinfo__section"
      >
        <p>{{ $t("errors.actionCouldNotBePlayed") }}</p>

        <ul class="impulse-sourceinfo__preventinglist">
          <li v-for="condition of preventingConditions" :key="condition.id">
            <condition-label :condition-id="condition.id" />
          </li>
        </ul>
      </section>
      <section
        v-if="item.type == 'action' && notPayableCost != null"
        class="impulse-sourceinfo__section"
      >
        <p>{{ $t("errors.actionCouldNotBePlayed") }}</p>
        <p>
          {{
            $t("texts.notEnough$resource", {
              resource: notPayableCost.element?.title,
              available: Math.round(notPayableCost.availableValue * 10) / 10,
              needed: notPayableCost.neededValue
            })
          }}
        </p>
      </section>

      <section
        v-if="isActivated && costImpulses.length > 0"
        class="impulse-sourceinfo__section"
      >
        <div v-if="showTitle" class="subtitle line">
          <span>
            {{ $t("labels.costs") }}
          </span>
          <span
            v-if="!round.played"
            class="impulse-sourceinfo__unplayed-icon"
            :title="$t('texts.XNotPlayedYet', [$t(`roundUnits.${roundUnit}`)])"
          >
            <lucide-icon
              name="triangle-alert"
            />
          </span>
        </div>

        <div class="list">
          <nesting-impulse
            v-for="impulse in costImpulses"
            :key="impulse.elementUid"
            :impulses="simulationImpulses"
            :source-type="sourceType"
            :source-id="item.id"
            :simulation-id="simulationId"
            :round-index="roundIndex + roundOffset"
            :affected-type="impulse.affectedType"
            :affected-id="impulse.affectedId"
            :force-visible="true"
            :before-visible="false"
          />
        </div>
      </section>

      <!-- impulse EFFECTS -->
      <section
        v-if="isActivated && effectImpulses.length > 0"
        class="impulse-sourceinfo__section"
      >
        <div v-if="showTitle" class="subtitle line">
          <span>{{ $t("objectNames.effect", 3) }}</span>
          <span
            v-if="!round.played"
            class="impulse-sourceinfo__unplayed-icon"
            :title="$t('texts.XNotPlayedYet', [$t(`roundUnits.${roundUnit}`)])"
          >
            <lucide-icon
              name="triangle-alert"
            />
          </span>
        </div>

        <p v-if="hasZeroEffect" class="mv-1">
          {{ $t("texts.noEffect") }}
        </p>
        <div v-else class="list">
          <nesting-impulse
            v-for="impulse in effectImpulses"
            :key="impulse.id"
            :impulses="simulationImpulses"
            :source-type="sourceType"
            :source-id="item.id"
            :simulation-id="simulationId"
            :round-index="roundIndex + roundOffset"
            :affected-type="impulse.affectedType"
            :affected-id="impulse.affectedId"
            :force-visible="true"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { notNull } from "@/helpers/utility/Type";
import { isEmpty } from "@/helpers/utility/Text";

import LucideIcon from "@core/ui/LucideIcon.vue";

import FormattedText from "@core/ui/FormattedText.vue";
import NestingImpulse from "@editor/items/impulse/NestingImpulse.vue";
import ConditionLabel from "@editor/items/condition/ConditionLabel.vue";
import TriggerTitle from "@editor/items/incidentTrigger/TriggerTitle.vue";

import { useEffectStore } from "@editor/items/effect/effects.store";
import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useIncidentTriggerStore } from "@editor/items/incidentTrigger/incidentTriggers.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useElements } from "../element/elements";

import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { ScheduledEffectItem } from "../scheduledEffect/ScheduledEffectItem";
import type { IncidentItem } from "../incident/IncidentItem";
import type { ActionItem } from "../action/ActionItem";
import type { EffectItem } from "../effect/EffectItem";
import { splitUid } from "@/helpers/utility/Id";
import type { PendingSimulationRoundEffect } from "../simulationRound/SimulationRoundItem";

const props = withDefaults(defineProps<{
  simulationId: number;
  roundIndex: number;
  roundOffset?: number;
  item: ScheduledEffectItem | IncidentItem | ActionItem | PendingSimulationRoundEffect;
  showTitle?: boolean;
}>(), {
  roundOffset: 0,
  showTitle: true
});

const elements = useElements();
const simulation = computed(() => {
  return useSimulationStore().items[props.simulationId];
});
const round = computed(() => {
  return simulation.value.rounds?.[props.roundIndex + props.roundOffset];
});
const roundUnit = computed(() => {
  return simulation.value.roundUnit;
});
const isActivated = computed(() => {
  if (props.item.type === "incident") {
    return true;
  }
  if (simulation.value.complete) {
    return props.roundIndex <= simulation.value.currentRoundIndex;
  }
  return props.roundIndex < simulation.value.currentRoundIndex;
});
const effects = computed((): (ScheduledEffectItem | EffectItem)[] => {
  if (props.item.type === "scheduledEffect") {
    return [props.item];
  }
  return useEffectStore().getByActivator(props.item) || [];
});
const hasZeroEffect = computed(() => {
  return effects.value.every(
    (effect) =>
      effect.impulseExpressions.length == 0 ||
      effect.impulseExpressions[props.roundOffset] == ""
  );
});
const simulationImpulses = computed(() => {
  return useSimulationStore().getImpulses(props.simulationId);
});
const effectImpulses = computed(() => {
  const impulses = simulationImpulses.value;

  // get only the impulses for this round, item type and item
  const currentRoundImpulses = impulses[
    props.roundIndex + props.roundOffset
  ] || {
    target: {},
    resource: {}
  };

  const effectImpulses = effects.value.map((effect) => {
    const impulse =
      currentRoundImpulses?.[effect.affectedType]?.[effect.affectedId];
    if (impulse == null) return null;

    // get the part of the impulse that comes from the item.
    const baseImpulse = impulse.sourceValues.find(
      (source) => source.type == props.item.type && source.id == props.item.id && source.effectType == "effect"
    );
    return {
      id: effect.id,
      affectedType: effect.affectedType,
      affectedId: effect.affectedId,
      impulse,
      baseImpulse
    };
  });

  return effectImpulses.filter(notNull);
});

const preventingConditions = computed(() => {
  if (props.item.type !== "action") return [];
  const prevented = round.value?.preventedActions;
  if (!prevented) return [];
  if (prevented[props.item.id] == null) return [];
  if (prevented[props.item.id].conditionIds == null) return [];
  if (prevented[props.item.id].conditionIds?.length == 0) return [];
  const conditionIds = prevented[props.item.id].conditionIds || [];
  return conditionIds.map((id) => useConditionStore().items[id]);
});

const notPayableCost = computed(() => {
  if (props.item.type !== "action") return null;

  const prevented = round.value?.notPayableActions;
  if (!prevented) return null;

  const preventedAction = prevented[props.item.id];
  if (preventedAction == null) return null;

  const element = elements.byUid[preventedAction.elementUid];

  return {
    element,
    availableValue: preventedAction.availableValue,
    neededValue: preventedAction.neededValue
  };
});

const incidentReason = computed(() => {
  if (props.item.type !== "incident") return null;

  const activatedIncidents = round.value?.activatedIncidents;
  if (!activatedIncidents) return null;

  const incidentReason = activatedIncidents.find(
    (r) => r.incidentId == props.item.id
  );
  return incidentReason;
});

const costs = computed(() => {
  if (props.item.type != "action") return [];
  if (!("costs" in props.item)) return [];
  return props.item.costs;
});

const costImpulses = computed(() => {
  const impulses = simulationImpulses.value;

  // get only the impulses for this round, item type and item
  const currentRoundImpulses = impulses[
    props.roundIndex + props.roundOffset
  ] || {
    target: {},
    resource: {}
  };

  const costImpulses = costs.value.map((cost) => {
    const { type, id } = splitUid(cost.elementUid);
    const impulse = currentRoundImpulses[type][id];
    if (impulse == null) return null;

    // get the part of the impulse that comes from the item.
    const baseImpulse = impulse.sourceValues.find(
      (source) => source.type == props.item.type && source.id == props.item.id && source.effectType == "cost"
    );
    return {
      elementUid: cost.elementUid,
      expression: cost.expression,
      impulse,
      baseImpulse,
      affectedType: type,
      affectedId: id
    };
  });

  return costImpulses.filter(notNull);
});

const sourceType = computed(() => {
  if (props.roundOffset != 0) return `delayed_${props.item.type}`;
  return props.item.type;
});

const itemTitle = computed(() => {
  if (!("title" in props.item)) return "";
  return props.item.title;
});

const triggers = computed(() => {
  return useIncidentTriggerStore().items;
});
  
  
function openOverlay(item: OverlayDialogItemRequest | null) {
  useOverlay().setItem(item);
}
</script>

<style lang="scss">
.impulse-sourceinfo {
  max-width: 100%;
  width: 20rem;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;

  &__title {
    margin-top: 0.5rem;
    padding: 0 0.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.25rem;
  }

  &__section {
    padding: 0.5rem;
  }
  &__section:has(~ .impulse-sourceinfo__section) {
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--color-grey);
  }

  &__content {
    border-top: 1px solid var(--color-grey);
    margin-top: -1px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  &__preventinglist {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    line-height: 1.3;
    padding-left: 2rem;
  }

  &__infoicon {
    margin-left: 0.5rem;
  }

  &__unplayed-icon {
    margin-left: auto;
    color: var(--color-text-light);
  }
}
</style>
